import { cloneDeep } from "lodash";
import { BasicState, DynamicManager } from "../common/DynamicManager";
import { FieldName, FieldNames, FieldValue, FieldValues } from "../Schema";


export interface DynamicValuesManagerState extends BasicState {
  value: FieldValue;
}

export abstract class DynamicValuesManager<S extends DynamicValuesManagerState> extends DynamicManager<S> {

  /**
   * Dynamic field values that might be dynamically changed by the DynamicValuesManager.
   * It"s due to Data manager being specific and being able to modify
   * values of fields themselves.
   */
  dynamicValues: FieldValues = {};

  /**
   * Customized method to use dynamic values field in the updateFieldState.
   * This ensures that any dynamic properties depending on the updated field will receive
   * the most current values when they are executed.
   * 
   * @param name - The name of the field to update.
   * @param values - The current values of all fields before the update (not used).
   * @param triggerFields - List of fields that triggered the update.
   * @param skipSubscriptions - Whether to skip updating subscribed field states or not.
   */
  async updateFieldState(
    name: FieldName,
    values: FieldValues,
    triggerFields: FieldNames = [],
    skipSubscriptions: boolean = false,
    overrideDynamicValues: boolean = false
  ) {
    if (overrideDynamicValues) {
      this.dynamicValues = values;
    }
    await super.updateFieldState(
      name,
      this.dynamicValues,
      triggerFields,
      skipSubscriptions
    );
  }

  /**
   * Function to get the state of a specific field.
   *
   * @param name - Name of the field to get the state for.
   * @template S - The type of the state object that will be used by Manager
   *               and generated by Schema.
   */
  setFieldState(
    name: FieldName,
    newState: S
  ) {
    this.state[name] = {
      ...newState,
    };
    this.dynamicValues[name] = newState.value;
  }

  /**
   * Customized method to use dynamic values field in the updateSubscribedFieldStates.
   * This ensures that any dynamic properties depending on the updated field will receive
   * the most current values when they are executed.
   *
   * @param name - The name of the field that triggered the update.
   * @param values - The current values of all fields before the update.
   * @param triggerFields - List of fields that triggered the subscription update.
   */
  async updateSubscribedFieldStates(
    name: FieldName,
    _values: FieldValues,
    triggerFields: FieldNames
  ) {
    await super.updateSubscribedFieldStates(name, this.dynamicValues, triggerFields);
  }

  /**
   * Customized method to use dynamic values field in the updateState.
   * This ensures that any dynamic properties depending on the updated field will receive
   * the most current values when they are executed.
   * 
   * @param values - Values of fields to update the state with.
   * @param initial - Flag indicating if this is an initial state update.
   * @returns Promise that resolves when the state update is complete.
   */
  async updateState(
    values: FieldValues,
    initial: boolean = false
  ) {
    this.dynamicValues = cloneDeep(values);
    await super.updateState(values, initial);
  }

  /**
   * Retrieves the current values of all fields in the state.
   *
   * @returns An object containing the current values of all fields.
   */
  getValues(): FieldValues {
    const values: FieldValues = {};
    for (const fieldName in this.state) {
      values[fieldName] = this.state[fieldName].value;
    }
    return cloneDeep(values);
  }

  /**
   * Retrieves the current value of a specific field.
   *
   * @param name - The name of the field to retrieve the value for.
   * @returns The current value of the specified field.
   */
  getFieldValue(name: FieldName): FieldValue {
    return cloneDeep(this.state[name]?.value);
  }

  /**
   * Sets value of a specific field and triggers update.
   * 
   * @param name - The name of the field to set the value for.
   * @param value - The value to set for the field.
   */
  async setFieldValue(
    name: FieldName,
    newValue: FieldValue
  ) {
    this.dynamicValues[name] = newValue;
    await this.updateFieldState(
      name,
      this.dynamicValues
    );
  }
}